import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Code as CodeIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { DEVELOPER_THEME } from '../../types/common';
import MaskedTokenValue from './MaskedTokenValue';

interface DeveloperInfoSectionProps {
  title?: string;
  data: Record<string, any>;
}

const DeveloperInfoSection: React.FC<DeveloperInfoSectionProps> = ({
  title = "開発者情報",
  data
}) => {
  const { isDeveloper } = useAuth();
  // 状態管理: どのクライアントIDが表示されているか
  const [ visibleClientIds, setVisibleClientIds ] = useState<Record<string, boolean>>({});
  // 状態管理: どのトークン値が表示されているか
  const [ visibleTokens, setVisibleTokens ] = useState<Record<string, boolean>>({});
  // 開発者モードでない場合は何も表示しない
  if (!isDeveloper()) {
    return null;
  }
  // 日時フォーマット用のオプション
  const timestampFormatOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  };
  // UNIXタイムスタンプまたは日時文字列をJST日時に変換する関数
  const formatTimestamp = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return 'なし';
    try {
      let date: Date;
      if (typeof value === 'number') {
        // UNIXタイムスタンプの場合（秒単位）
        date = new Date(value * 1000); // ミリ秒に変換
      } else {
        // 日時文字列の場合
        date = new Date(value);
        // 日付が無効な場合
        if (isNaN(date.getTime())) {
          return String(value);
        }
      }
      return date.toLocaleString('ja-JP', timestampFormatOptions);
    } catch (e) {
      // 変換に失敗した場合は元の値を文字列で返す
      return String(value);
    }
  };
  // フィールドの値を大きいフォントで表示する関数
  const formatLargeValue = (value: any) => {
    return (
      <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
        {value}
      </span>
    );
  };
  // client_id表示/非表示を切り替える
  const toggleClientIdVisibility = (key: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setVisibleClientIds(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  // すべてのトークンキーをマスクする関数
  const maskClientId = (key: string): React.ReactNode => {
    const isVisible = visibleClientIds[key] || false;
    const refreshIndex = key.indexOf('_refresh');
    // 表示/非表示のためのコンポーネント
    const visibilityControl = (
      <Tooltip title={isVisible ? "IDを隠す" : "IDを表示する"}>
        <IconButton
          size="small"
          onClick={(e) => toggleClientIdVisibility(key, e)}
          sx={{ ml: 0.5 }}
          className="no-propagation"
        >
          {isVisible ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
        </IconButton>
      </Tooltip>
    );
    if (refreshIndex > 0) {
      // _refreshを含む場合は特別処理（_refreshのみ表示）
      const clientId = key.substring(0, refreshIndex);
      const suffix = key.substring(refreshIndex); // _refresh部分
      // client_idの一部だけマスク処理
      const partialMask = clientId.length > 6
        ? clientId.substring(0, 2) + '***' + clientId.substring(clientId.length - 2)
        : clientId.substring(0, 1) + '***';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <span>{isVisible ? clientId : partialMask}</span>
          <span>{suffix}</span>
          {visibilityControl}
        </Box>
      );
    } else {
      // 通常のトークンキーは一部だけマスク処理
      const partialMask = key.length > 6
        ? key.substring(0, 2) + '***' + key.substring(key.length - 2)
        : key.substring(0, 1) + '***';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <span>{isVisible ? key : partialMask}</span>
          {visibilityControl}
        </Box>
      );
    }
  };
  // FCMトークンを表示するためのコンポーネント
  const FCMTokenDisplay = ({ token }: { token: any }) => {
    const [ isVisible, setIsVisible ] = useState(false);
    // オブジェクトをグループに分けて表示
    const renderStructuredContent = (tokenObj: any) => {
      // トークンフィールドの表示
      const renderTokenField = () => {
        const value = tokenObj['token'] ?? 'null';
        // トークンがなくても表示枠は作る
        let displayValue = value;
        if (value !== 'null' && typeof value === 'string') {
          const tokenStr = String(value);
          // 表示状態に応じて値を変更
          displayValue = !isVisible && tokenStr.length > 15
            ? `${tokenStr.substring(0, 6)}...${tokenStr.substring(tokenStr.length - 6)}`
            : tokenStr;
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ marginRight: '8px', width: '60px' }}>token:</span>
            <span style={{ marginRight: '8px', fontFamily: 'monospace', fontSize: '1rem' }}>{displayValue}</span>
            {value !== 'null' && (
              <Tooltip title={isVisible ? "トークンを隠す" : "トークンを表示する"}>
                <IconButton
                  size="small"
                  onClick={() => setIsVisible(!isVisible)}
                  sx={{ p: 0.5 }}
                >
                  {isVisible ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      };
      // 日時フィールドをまとめて表示（last_sent_at, updated_at, created_atの順）
      const renderDateGroup = () => {
        const dateFields = [ 'last_sent_at', 'updated_at', 'created_at' ];
        // 常に項目は表示する
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
            {dateFields.map(field => {
              const value = tokenObj[field] ? formatTimestamp(tokenObj[field]) : 'null';
              return (
                <div key={field} style={{ display: 'flex', minWidth: '300px' }}>
                  <span style={{ marginRight: '8px', width: '95px' }}>{field}:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>{value}</span>
                </div>
              );
            })}
          </div>
        );
      };
      // デバイス情報グループ（OSとバージョン）
      const renderOsGroup = () => {
        const osFields = [ 'os', 'os_version' ];
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
            {osFields.map(field => {
              const value = tokenObj[field] ?? 'null';
              return (
                <div key={field} style={{ display: 'flex', minWidth: '250px' }}>
                  <span style={{ marginRight: '8px', width: '80px' }}>{field}:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>{value}</span>
                </div>
              );
            })}
          </div>
        );
      };
      // デバイス詳細グループ（名前、モデル、製造元）
      const renderDeviceGroup = () => {
        const deviceFields = [ 'name', 'model', 'manufacturer' ];
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
            {deviceFields.map(field => {
              const value = tokenObj[field] ?? 'null';
              // manufacturerの場合は幅を広げる
              const labelWidth = field === 'manufacturer' ? '110px' : '80px';
              return (
                <div key={field} style={{ display: 'flex', minWidth: '250px' }}>
                  <span style={{ marginRight: '8px', width: labelWidth }}>{field}:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>{value}</span>
                </div>
              );
            })}
          </div>
        );
      };
      // その他のフィールド
      const renderOtherFields = () => {
        const excludedFields = [ 'token', 'created_at', 'updated_at', 'last_sent_at', 'os', 'os_version', 'name', 'model', 'manufacturer' ];
        // 元のオブジェクトにないフィールドも取得する
        const otherFields = Object.keys(tokenObj).filter(key => !excludedFields.includes(key));
        if (otherFields.length === 0) return null;
        return (
          <div style={{ marginTop: '8px' }}>
            {otherFields.map(key => {
              const value = tokenObj[key] ?? 'null';
              return (
                <div key={key} style={{ marginBottom: '8px' }}>
                  <span style={{ marginRight: '8px' }}>{key}:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
                    {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              );
            })}
          </div>
        );
      };
      return (
        <>
          {renderTokenField()}
          {renderDateGroup()}
          {renderOsGroup()}
          {renderDeviceGroup()}
          {renderOtherFields()}
        </>
      );
    };
    // コンテンツをレンダリング
    const renderTokenContent = () => {
      if (typeof token === 'object') {
        return renderStructuredContent(token);
      } else {
        // 文字列の場合はシンプルに表示
        const tokenStr = String(token);
        const displayValue = !isVisible && tokenStr.length > 15
          ? `${tokenStr.substring(0, 6)}...${tokenStr.substring(tokenStr.length - 6)}`
          : tokenStr;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px', width: '60px' }}>token:</span>
            <span style={{ marginRight: '8px', fontFamily: 'monospace', fontSize: '1rem' }}>{displayValue}</span>
            <Tooltip title={isVisible ? "トークンを隠す" : "トークンを表示する"}>
              <IconButton
                size="small"
                onClick={() => setIsVisible(!isVisible)}
                sx={{ p: 0.5 }}
              >
                {isVisible ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
              </IconButton>
            </Tooltip>
          </div>
        );
      }
    };
    return (
      <Box sx={{ my: 0.5 }}>
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            p: 1,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        >
          {renderTokenContent()}
        </Box>
      </Box>
    );
  };
  // FCMトークン配列を表示するための関数
  const formatFCMTokens = (tokens: any[]) => {
    if (!tokens || tokens.length === 0) {
      return <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>登録されたトークンがありません</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {tokens.map((token, index) => (
          <FCMTokenDisplay key={index} token={token}/>
        ))}
      </Box>
    );
  };
  // フィールド値を適切な形式で表示するためのフォーマッター
  const formatFieldValue = (value: any, fieldName?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>null</Typography>;
    }
    // user_idの場合の特別処理
    if (fieldName === 'user_id') {
      return (
        <Box sx={{ pl: 0, fontFamily: 'monospace', fontSize: '1rem' }}>
          {value}
        </Box>
      );
    }
    // FCMトークンの特別処理
    if (fieldName === 'fcm_tokens' && value !== null && value !== undefined) {
      // 特定のパターンを確認: トークンデータの場合の特別処理
      if (value && typeof value === 'object' && Object.keys(value).some(key =>
        [ 'token', 'created_at', 'updated_at', 'last_sent_at' ].includes(key))) {
        // トークンオブジェクトそのものの場合
        return <FCMTokenDisplay token={value}/>;
      }
      // FCMトークンの配列の場合
      if (Array.isArray(value)) {
        return formatFCMTokens(value);
      } else if (value && typeof value === 'object') {
        // FCMトークンがオブジェクトの場合（キーがデバイスIDなど）
        return (
          <TableContainer sx={{ ml: 2, maxWidth: 'calc(100% - 16px)' }}>
            <Table size="small">
              <TableBody>
                {Object.entries(value).map(([ deviceId, tokens ], index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell
                      component="th"
                      scope="row"
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        width: '30%',
                        py: 1,
                        pr: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }}
                    >
                      デバイス {index + 1}:
                    </TableCell>
                    <TableCell align="left" sx={{ py: 1, color: 'text.primary' }}>
                      {Array.isArray(tokens)
                        ? formatFCMTokens(tokens as any[])
                        : <FCMTokenDisplay token={tokens}/>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }
      // 単一の値の場合（文字列や数値）
      return <FCMTokenDisplay token={value}/>;
    }
    if (typeof value === 'object' && 'token' in value && 'expiry' in value) {
      // トークンオブジェクトの場合、構造化して表示
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* token, expiry, extras を横並びに */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '300px' }}>
              <Typography component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1, minWidth: '60px' }}>
                token:
              </Typography>
              <MaskedTokenValue value={value.token}/>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '220px' }}>
              <Typography component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1, minWidth: '60px' }}>
                expiry:
              </Typography>
              <Typography component="span">{formatTimestamp(value.expiry)}</Typography>
            </Box>

            {value.token_extras && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1, minWidth: '60px' }}>
                  extras:
                </Typography>
                <Typography component="span"
                            sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}>
                  {JSON.stringify(value.token_extras, null, 2)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      );
    }
    // トークンオブジェクトの中のトークンマップを処理
    if (typeof value === 'object' && Object.keys(value).some(key =>
      key.includes('_refresh') || typeof value[key] === 'object' && 'token' in value[key])) {
      return (
        <TableContainer sx={{ ml: 2, maxWidth: 'calc(100% - 16px)' }}>
          <Table size="small">
            <TableBody>
              {Object.entries(value).map(([ tokenKey, tokenValue ]) => (
                <TableRow key={tokenKey} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    align="right"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      width: '25%',
                      py: 1,
                      pr: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}
                  >
                    {maskClientId(tokenKey)}
                  </TableCell>
                  <TableCell align="left" sx={{ py: 1, color: 'text.primary' }}>
                    {formatFieldValue(tokenValue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
    // その他の一般的なオブジェクトの場合はJSONで表示
    if (typeof value === 'object') {
      // トークン関連のフィールドは特別にカスタムフォーマット
      if (fieldName === 'tokens (for auth)') {
        return (
          <Box sx={{ fontFamily: 'monospace', fontSize: '1rem', pl: 0 }}>
            {Object.entries(value).map(([ tokenKey, tokenObj ]) => (
              <Box key={tokenKey} sx={{ mb: 2 }}>
                <Typography component="div" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                  {tokenKey}:
                </Typography>
                {Object.entries(tokenObj as Record<string, any>).map(([ k, v ]) => (
                  <Box key={k} sx={{ display: 'flex', mb: 0.5 }}>
                    <Box sx={{ width: '80px', color: 'text.secondary', mr: 2 }}>
                      {k}:
                    </Box>
                    <Box sx={{ flex: 1, fontFamily: 'monospace' }}>
                      {k === 'expiry' ? formatTimestamp(v as number) : String(v)}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        );
      }
      // 通常のJSONオブジェクト
      return (
        <Typography component="pre"
                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, overflowX: 'auto' }}>
          {JSON.stringify(value, null, 2)}
        </Typography>
      );
    }
    // 通常の値はそのまま文字列として表示
    return String(value);
  };
  // HTMLを直接使って配置を強制的に制御
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px dashed ${DEVELOPER_THEME.BORDER_ALPHA}`,
        backgroundColor: DEVELOPER_THEME.BACKGROUND_ALPHA,
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: DEVELOPER_THEME.DARK }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CodeIcon sx={{ mr: 1, fontSize: '1.2rem' }}/>
          <Typography variant="h6" component="h2" sx={{ color: DEVELOPER_THEME.DARK }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          すべての日時はJST（日本時間）で表示
        </Typography>
      </Box>

      <Divider sx={{ borderColor: DEVELOPER_THEME.BORDER_LIGHT_ALPHA }}/>

      <CardContent>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
          {Object.entries(data).map(([ key, value ], index) => (
            <React.Fragment key={key}>
              {index > 0 && (
                <tr>
                  <td colSpan={2} style={{ padding: 0 }}>
                    <Divider sx={{ my: 2 }}/>
                  </td>
                </tr>
              )}
              <tr style={{ verticalAlign: 'middle' }}>
                <td style={{
                  width: '180px',
                  paddingRight: '16px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: 'bold',
                  textAlign: 'right',
                  verticalAlign: 'inherit' // 親要素の設定を継承
                }}>
                  {key}
                </td>
                <td style={{
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  fontFamily: 'monospace',
                  fontSize: '1rem'
                }}>
                  {key === 'user_id' ? (
                    value
                  ) : key === 'tokens (for auth)' ? (
                    <div>
                      {Object.entries(value as Record<string, any>).map(([ tokenKey, tokenObj ], index) => (
                        <div key={index} style={{
                          marginBottom: '16px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '12px'
                        }}>
                          <div style={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            marginBottom: '8px',
                            fontWeight: 'normal'
                          }}>
                            {tokenKey}:
                          </div>
                          <div style={{ marginLeft: '0px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                              {/* token と expiry を横並びに */}
                              {Object.keys(tokenObj as Record<string, any>).includes('token') && (
                                <div style={{ display: 'flex', minWidth: '350px', alignItems: 'center' }}>
                                  <div style={{ width: '60px', color: 'rgba(0, 0, 0, 0.6)' }}>token:</div>
                                  <div style={{ fontFamily: 'monospace', marginRight: '8px' }}>
                                    {visibleTokens[`${tokenKey}_token`]
                                      ? String(tokenObj.token)
                                      : String(tokenObj.token).length > 12
                                        ? `${String(tokenObj.token).substring(0, 6)}...${String(tokenObj.token).substring(String(tokenObj.token).length - 6)}`
                                        : String(tokenObj.token)
                                    }
                                  </div>
                                  <Tooltip title={visibleTokens[`${tokenKey}_token`] ? "トークンを隠す" : "トークンを表示する"}>
                                    <IconButton
                                      size="small"
                                      onClick={() => setVisibleTokens(prev => ({
                                        ...prev,
                                        [`${tokenKey}_token`]: !prev[`${tokenKey}_token`]
                                      }))}
                                      sx={{ p: 0.5 }}
                                    >
                                      {visibleTokens[`${tokenKey}_token`] ? <VisibilityOffIcon fontSize="small"/> :
                                        <VisibilityIcon fontSize="small"/>}
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )}

                              {Object.keys(tokenObj as Record<string, any>).includes('expiry') && (
                                <div style={{ display: 'flex', minWidth: '350px' }}>
                                  <div style={{ width: '60px', color: 'rgba(0, 0, 0, 0.6)' }}>expiry:</div>
                                  <div style={{ fontFamily: 'monospace' }}>
                                    {formatTimestamp(tokenObj.expiry as number)}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* その他のフィールド */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
                              {/* token_extrasは特別扱い（横並びに表示） */}
                              {Object.entries(tokenObj as Record<string, any>)
                              .filter(([ k, v ]) => k === 'token_extras' && v !== null && v !== undefined)
                              .map(([ k, v ], i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', minWidth: '350px' }}>
                                  <div style={{
                                    width: '95px',
                                    color: 'rgba(0, 0, 0, 0.6)',
                                  }}>
                                    {k}:
                                  </div>
                                  <div style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    backgroundColor: '#f5f5f5',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    maxHeight: '80px',
                                    overflowY: 'auto',
                                    maxWidth: '500px',
                                    overflowX: 'auto'
                                  }}>
                                      <pre style={{ margin: 0 }}>
                                        {JSON.stringify(v, null, 2)}
                                      </pre>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* token_extras以外のフィールド */}
                            {Object.entries(tokenObj as Record<string, any>)
                            .filter(([ k ]) => k !== 'token' && k !== 'expiry' && k !== 'token_extras')
                            .map(([ k, v ], i) => (
                              <div key={i} style={{ display: 'flex', marginTop: '8px' }}>
                                <div style={{
                                  width: '80px',
                                  paddingRight: '16px',
                                  color: 'rgba(0, 0, 0, 0.6)',
                                }}>
                                  {k}:
                                </div>
                                <div style={{
                                  fontFamily: 'monospace'
                                }}>
                                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                </div>
                              </div>
                            ))
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : key === 'fcm_tokens' ? (
                    <div>
                      {formatFieldValue(value, key)}
                    </div>
                  ) : (
                    formatFieldValue(value, key)
                  )}
                </td>
              </tr>
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
export default DeveloperInfoSection;
